import {
  AsyncMultiSelectField,
  AsyncSingleSelectField,
  CharField,
  MultiSelectField,
  NumberField,
  RadiosField,
  SingleSelectField,
  TextAreaField,
} from "@inmagik/magik-ui"
import { getIn, useFormikContext } from "formik"
import { Col } from "react-bootstrap"
import { Paratesto } from "../../types"
import { useEdizioneCreate, useEdizioneLazy, useEdizioniLazy } from "../../hooks/edizioni"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import EdizioneModal from "../Edizioni/EdizioneModal"
import { usePersonaCreate, usePersonaLazy, usePersoneLazy } from "../../hooks/persone"
import PersonaModal from "../Persone/PersonaModal"
import { transformErrorForForm } from "../../utils/utils"
import { WysiwygField } from "../../components/WysiwygField"

export default function ParatestoForm({ col, showEdizione = false }: { col?: number; showEdizione?: boolean }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Paratesto>()

  const fetchEdizione = useEdizioneLazy()
  const fetchEdizioni = useEdizioniLazy()

  const [modalStateCreateEdizione, modalActionsCreateEdizione] = useModalTrigger()
  const [modalStateCreatePersona, modalActionsCreatePersona] = useModalTrigger()
  const createEdizione = useEdizioneCreate()
  const createPersona = usePersonaCreate()

  const fetchPersona = usePersonaLazy()
  const fetchPersone = usePersoneLazy()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      {showEdizione && (
        <AsyncSingleSelectField
          label="Edizione"
          layout="horizontal"
          placeholder="Seleziona un edizione"
          onChange={(value) => {
            setFieldValue("edizione", value)
          }}
          clearable
          value={values.edizione}
          loadOptions={(inputValue: string) => {
            return fetchEdizioni({ search: inputValue }).then((edizioni) => {
              return edizioni.results?.map((edizione) => ({
                label: edizione.opera_data?.titolo + " - " + edizione.titolo_completo,
                value: edizione.id,
              }))
            })
          }}
          loadInitialValue={(id: number) =>
            fetchEdizione(id).then((edizione) => {
              return { label: edizione.opera_data?.titolo + " - " + edizione.titolo_completo, value: edizione.id }
            })
          }
          required
          isValid={submitCount > 0 ? !getIn(errors, "edizione") : undefined}
          errorMessage={getIn(errors, "edizione")}
        >
          <SingleSelectField.Add
            onClick={() => {
              modalActionsCreateEdizione.open()
            }}
          />
        </AsyncSingleSelectField>
      )}
      <RadiosField
        label="Tipologia"
        layout="horizontal"
        options={[
          { label: "Legales", value: "Legales" },
          { label: "Socio-literarios", value: "Socio-literarios" },
          { label: "Editoriales", value: "Editoriales" },
          { label: "Textuales", value: "Textuales" },
        ]}
        value={values.tipologia}
        onChange={(value) => {
          setFieldValue("tipologia", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "tipologia") : undefined}
        errorMessage={getIn(errors, "tipologia")}
        required
      />
      <CharField
        label="Categoria"
        layout="horizontal"
        value={values.categoria}
        onChange={(value) => {
          setFieldValue("categoria", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "categoria") : undefined}
        errorMessage={getIn(errors, "categoria")}
      />
      <WysiwygField
        label="Descrizione"
        layout="horizontal"
        required
        value={values.descrizione}
        onChange={(value) => {
          setFieldValue("descrizione", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "descrizione") : undefined}
        errorMessage={getIn(errors, "descrizione")}
      />
      <WysiwygField
        label="Titolo"
        layout="horizontal"
        value={values.titolo}
        onChange={(value) => {
          setFieldValue("titolo", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "titolo") : undefined}
        errorMessage={getIn(errors, "titolo")}
      />
      <CharField
        label="Ubicazione"
        layout="horizontal"
        value={values.ubicazione}
        onChange={(value) => {
          setFieldValue("ubicazione", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "ubicazione") : undefined}
        errorMessage={getIn(errors, "ubicazione")}
      />
      <AsyncMultiSelectField
        label="Autori"
        layout="horizontal"
        placeholder="Seleziona uno o più autori"
        onChange={(value) => {
          setFieldValue("autori", value)
        }}
        clearable
        value={values.autori}
        loadInitialValues={(ids: number[]) =>
          Promise.all(ids.map((id) => fetchPersona(id).then((persona) => ({ label: persona.nome, value: persona.id }))))
        }
        loadOptions={(inputValue: string) => {
          return fetchPersone({ search: inputValue }).then((persone) => {
            return persone.results.map((persona) => ({
              label: persona.nome,
              value: persona.id,
            }))
          })
        }}
      >
        <MultiSelectField.Add
          onClick={() => {
            modalActionsCreatePersona.open("autori")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Firmatari"
        layout="horizontal"
        placeholder="Seleziona uno o più firmatari"
        onChange={(value) => {
          setFieldValue("firmatari", value)
        }}
        clearable
        value={values.firmatari}
        loadInitialValues={(ids: number[]) =>
          Promise.all(ids.map((id) => fetchPersona(id).then((persona) => ({ label: persona.nome, value: persona.id }))))
        }
        loadOptions={(inputValue: string) => {
          return fetchPersone({ search: inputValue }).then((persone) => {
            return persone.results.map((persona) => ({
              label: persona.nome,
              value: persona.id,
            }))
          })
        }}
      >
        <MultiSelectField.Add
          onClick={() => {
            modalActionsCreatePersona.open("firmatari")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncSingleSelectField
        label="Traduttore"
        layout="horizontal"
        placeholder="Seleziona un traduttore"
        onChange={(value) => {
          setFieldValue("traduttore", value)
        }}
        clearable
        value={values.traduttore}
        loadOptions={(inputValue: string) => {
          return fetchPersone({ search: inputValue }).then((persone) => {
            return persone.results.map((persona) => ({
              label: persona.nome,
              value: persona.id,
            }))
          })
        }}
        loadInitialValue={(id: number) =>
          fetchPersona(id).then((persona) => {
            return { label: persona.nome, value: persona.id }
          })
        }
      >
        <SingleSelectField.Add
          onClick={() => {
            modalActionsCreatePersona.open("traduttore")
          }}
        />
      </AsyncSingleSelectField>
      <CharField
        label="Explicit"
        layout="horizontal"
        value={values.explicit}
        onChange={(value) => {
          setFieldValue("explicit", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "explicit") : undefined}
        errorMessage={getIn(errors, "explicit")}
      />
      <WysiwygField
        label="Testo"
        layout="horizontal"
        value={values.testo}
        rows={10}
        onChange={(value) => {
          setFieldValue("testo", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "testo") : undefined}
        errorMessage={getIn(errors, "testo")}
      />
      <NumberField
        label="Ordine"
        layout="horizontal"
        value={values.ordine}
        onChange={(value) => {
          setFieldValue("ordine", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "ordine") : undefined}
        errorMessage={getIn(errors, "ordine")}
        required
      />
      <EdizioneModal
        show={modalStateCreateEdizione.isOpen}
        onClose={modalActionsCreateEdizione.close}
        showOpera
        save={(values) => {
          return createEdizione.mutateAsync(values).then((edizione) => {
            setFieldValue("edizione", edizione.id)
            modalActionsCreateEdizione.close()
          })
        }}
        title="Crea edizione"
      />
      {modalStateCreateEdizione.isOpen && (
        <EdizioneModal
          show={modalStateCreateEdizione.isOpen}
          onClose={modalActionsCreateEdizione.close}
          showOpera={showEdizione}
          save={(values) => {
            return createEdizione.mutateAsync(values).then((edizione) => {
              setFieldValue("edizione", edizione.id)
              modalActionsCreateEdizione.close()
            })
          }}
          title="Crea edizione"
        />
      )}
      {modalStateCreatePersona.isOpen && (
        <PersonaModal
          show={modalStateCreatePersona.isOpen}
          onClose={modalActionsCreatePersona.close}
          save={(valuesModal, actions) => {
            return createPersona
              .mutateAsync(valuesModal)
              .then((persona) => {
                setFieldValue(
                  modalStateCreatePersona.value,
                  modalStateCreatePersona.value === "autori"
                    ? [...values.autori, persona.id]
                    : modalStateCreatePersona.value === "firmatari"
                    ? [...values.firmatari, persona.id]
                    : modalStateCreatePersona.value === "traduttore"
                    ? persona.id
                    : values.autori,
                )
                modalActionsCreatePersona.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title={`Nuovo ${modalStateCreatePersona.value}`}
        />
      )}
    </Col>
  )
}
